
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  XMarkIcon, 
  SmartphoneIcon, 
  ShareIcon, 
  PlusIcon,
  CheckCircleIcon
} from './Icons';

const PWAInstallPrompt: React.FC = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'other'>('other');
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        (window.navigator as any).standalone || 
                        document.referrer.includes('android-app://');
    
    setIsInstalled(isStandalone);

    // Detect platform
    const userAgent = window.navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setPlatform('ios');
    } else if (/android/.test(userAgent)) {
      setPlatform('android');
    }

    // Listen for beforeinstallprompt (Android)
    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay if not installed
      if (!isStandalone) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, we show it manually since there's no event
    if (!isStandalone && /iphone|ipad|ipod/.test(userAgent)) {
      // Check if we've already shown it in this session
      const hasShown = sessionStorage.getItem('pwa_prompt_shown');
      if (!hasShown) {
        setTimeout(() => {
          setShowPrompt(true);
          sessionStorage.setItem('pwa_prompt_shown', 'true');
        }, 5000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const closePrompt = () => {
    setShowPrompt(false);
  };

  if (isInstalled || !showPrompt || platform === 'other') return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-4 left-4 right-4 z-[100] md:left-auto md:right-4 md:w-96"
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 overflow-hidden relative">
          <button 
            onClick={closePrompt}
            className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon size={20} />
          </button>

          <div className="flex items-start gap-4">
            <div className="bg-primary/10 p-3 rounded-xl text-primary shrink-0">
              <SmartphoneIcon size={28} />
            </div>
            <div className="flex-1 pr-6">
              <h3 className="font-bold text-gray-900 text-lg leading-tight mb-1">
                Cài đặt ứng dụng
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Cài đặt ứng dụng HUD3 Linh Đàm vào màn hình chính để truy cập nhanh hơn và nhận thông báo.
              </p>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-gray-50">
            {platform === 'android' ? (
              <button
                onClick={handleInstallClick}
                className="w-full py-3 bg-primary text-white rounded-xl font-bold text-sm shadow-lg shadow-primary/20 hover:bg-primary-focus transition-all active:scale-[0.98] flex items-center justify-center gap-2"
              >
                <PlusIcon size={18} strokeWidth={3} />
                Cài đặt ngay
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm text-gray-700 bg-gray-50 p-3 rounded-xl">
                  <div className="bg-white p-1.5 rounded-lg shadow-sm">
                    <ShareIcon size={16} className="text-blue-500" />
                  </div>
                  <span>1. Nhấn vào nút <strong>Chia sẻ</strong> trên trình duyệt</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-700 bg-gray-50 p-3 rounded-xl">
                  <div className="bg-white p-1.5 rounded-lg shadow-sm">
                    <PlusIcon size={16} className="text-gray-700" />
                  </div>
                  <span>2. Chọn <strong>Thêm vào MH chính</strong> (Add to Home Screen)</span>
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-3 flex items-center justify-center gap-1.5 text-[10px] text-gray-400 font-medium uppercase tracking-wider">
            <CheckCircleIcon size={10} className="text-green-500" />
            Ứng dụng an toàn & bảo mật
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PWAInstallPrompt;
